import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  getImages,
  getImage,
  uploadImages,
  updateImage,
  deleteImage,
  bulkDeleteImages,
} from "api/imageApi"
import { VEHICLE_IMAGE_QUERY_KEY } from "./useShareVehicleImage"

export const IMAGES_QUERY_KEY = "images"

export const useImages = (params = {}) => {
  return useQuery({
    queryKey: [IMAGES_QUERY_KEY, params],
    queryFn: () => getImages(params),
    keepPreviousData: true,
  })
}

export const useImage = id => {
  return useQuery({
    queryKey: [IMAGES_QUERY_KEY, id],
    queryFn: () => getImage(id),
    enabled: !!id,
  })
}

export const useUploadImages = () => {
  return useMutation({
    mutationFn: uploadImages,
  })
}

export const useUpdateImage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateImage,
    onSuccess: data => {
      if (data?.success) {
        // Invalidate both the specific image and the images list
        queryClient.invalidateQueries([IMAGES_QUERY_KEY])
      }
    },
  })
}

export const useDeleteImage = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteImage,
    onSuccess: data => {
      if (data?.success) {
        queryClient.invalidateQueries([IMAGES_QUERY_KEY])
      }
    },
  })
}

export const useBulkDeleteImages = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: bulkDeleteImages,
    onSuccess: data => {
      if (data?.success) {
        queryClient.invalidateQueries([IMAGES_QUERY_KEY])
      }
    },
  })
}

/**
 * Update vehicle images cache in React Query
 * @param {string|number} vehicleId - Vehicle ID
 * @param {Array} images - Array of image objects [{ id, url, ... }]
 * @param {string|number|null} auctionId - Optional auction ID
 * @param {string} queryKeyBase - Base query key for vehicle images
 */
export function updateVehicleImagesCache({
  vehicleId,
  images = [],
  auctionId = null,
  queryClient,
}) {
  const queryKey = [VEHICLE_IMAGE_QUERY_KEY, vehicleId]

  queryClient.setQueryData(queryKey, oldData => {
    // Prepare new cache data in the correct format
    const newData = {
      vehicle_id: vehicleId,
      image_ids: images.map(img => img.id),
      auction_id: auctionId,
    }

    // Merge with oldData if needed
    return oldData
      ? {
          ...oldData,
          ...newData,
        }
      : newData
  })
}
